import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

export default function LoginPage() {
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user, isAdmin, isB2B } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && isAdmin) navigate('/admin');
    else if (user && isB2B) navigate('/mi-cuenta');
  }, [user, isAdmin, isB2B, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(err.message || 'Error al iniciar sesión. Verifica tus credenciales.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      if (password.length < 6) {
        throw new Error('La contraseña debe tener al menos 6 caracteres');
      }
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { name, role: 'b2b' },
        },
      });
      if (signUpError) throw signUpError;

      if (data.user) {
        await supabase.from('profiles').update({
          company: company || null,
          phone: phone || null,
        }).eq('id', data.user.id);
      }

      setSuccess('Cuenta creada exitosamente. Ya puedes iniciar sesión.');
      setIsRegister(false);
      setPassword('');
      setName('');
      setCompany('');
      setPhone('');
    } catch (err) {
      setError(err.message || 'Error al crear la cuenta.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setError('');
    setSuccess('');
    setEmail('');
    setPassword('');
    setName('');
    setCompany('');
    setPhone('');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
      className="min-h-screen pt-20 md:pt-24 pb-16 flex items-center justify-center bg-[#FAFAF7]"
    >
      <div className="w-full max-w-md px-4">
        <div className="bg-white rounded-2xl shadow-sm p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#2C5530] rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-2xl">GF</span>
            </div>
            <h1 className="font-cabinet font-bold text-2xl text-[#1A231C]">
              {isRegister ? 'Registro Cliente Empresa' : 'Acceso Gama Foods'}
            </h1>
            <p className="text-[#808E83] text-sm mt-2">
              {isRegister ? 'Crea tu cuenta para pedidos recurrentes' : 'Administrador o Cliente Empresa'}
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-xl text-sm">{error}</div>
          )}
          {success && (
            <div className="mb-6 p-4 bg-green-50 text-green-600 rounded-xl text-sm">{success}</div>
          )}

          {isRegister ? (
            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#4A5D4E] mb-1">Nombre completo *</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-3 border border-[#E8EAE6] rounded-xl focus:outline-none focus:border-[#2C5530] transition-colors"
                  placeholder="Tu nombre" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#4A5D4E] mb-1">Empresa</label>
                <input type="text" value={company} onChange={(e) => setCompany(e.target.value)}
                  className="w-full px-4 py-3 border border-[#E8EAE6] rounded-xl focus:outline-none focus:border-[#2C5530] transition-colors"
                  placeholder="Nombre de tu empresa (opcional)" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#4A5D4E] mb-1">Teléfono</label>
                <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-4 py-3 border border-[#E8EAE6] rounded-xl focus:outline-none focus:border-[#2C5530] transition-colors"
                  placeholder="+56 9 1234 5678 (opcional)" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#4A5D4E] mb-1">Email *</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-[#E8EAE6] rounded-xl focus:outline-none focus:border-[#2C5530] transition-colors"
                  placeholder="tu@email.com" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#4A5D4E] mb-1">Contraseña *</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-[#E8EAE6] rounded-xl focus:outline-none focus:border-[#2C5530] transition-colors"
                  placeholder="Mínimo 6 caracteres" required minLength={6} />
              </div>
              <button type="submit" disabled={loading}
                className="w-full bg-[#2C5530] hover:bg-[#3A6E40] disabled:bg-[#808E83] text-white font-medium py-3 px-6 rounded-full transition-colors">
                {loading ? 'Creando cuenta...' : 'Crear Cuenta Empresa'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#4A5D4E] mb-1">Email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-[#E8EAE6] rounded-xl focus:outline-none focus:border-[#2C5530] transition-colors"
                  placeholder="tu@email.com" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#4A5D4E] mb-1">Contraseña</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-[#E8EAE6] rounded-xl focus:outline-none focus:border-[#2C5530] transition-colors"
                  placeholder="••••••••" required />
              </div>
              <button type="submit" disabled={loading}
                className="w-full bg-[#2C5530] hover:bg-[#3A6E40] disabled:bg-[#808E83] text-white font-medium py-3 px-6 rounded-full transition-colors">
                {loading ? 'Ingresando...' : 'Ingresar'}
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <button onClick={() => { setIsRegister(!isRegister); resetForm(); }}
              className="text-sm text-[#2C5530] font-medium hover:underline">
              {isRegister ? 'Ya tengo cuenta, iniciar sesión' : '¿No tienes cuenta? Regístrate como Cliente Empresa'}
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
